/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, warp-drive/no-external-request-patterns */
// Extending From
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import config from '@linc-technologies/linced-common/utils/config';
import { LogLevel } from '@grafana/faro-web-sdk';

/**
 * fetch service provides a way to perform authenticated API requests that are
 * 'out-of-band' from ember-data. That is, where we just need to push or pull
 * some data that doesn't need to be ember-'datafied'.
 */
export default class FetchService extends Service {
  // Services
  @service
  session;

  @service
  logging;

  // Attrs
  contentType = 'application/json; charset=utf-8';

  trustedHosts = config.APP.trustedOrigins;

  // Properties
  // sets the x-tenant-id header to the current user's active school.
  @tracked
  useActiveSchoolID = false;

  @tracked
  headers;

  init() {
    super.init(...arguments);
    this.headers = {};
    let isSchoolApp = config.modulePrefix === 'linced-app';
    let isLearnerApp = config.modulePrefix === 'linced-learners';
    this.useActiveSchoolID = isSchoolApp || isLearnerApp;
  }

  // Simple function to perform a POST request
  post(url, options) {
    return this.performRequest(url, 'POST', options);
  }

  // Simple function to perform a PUT request
  put(url, options) {
    return this.performRequest(url, 'PUT', options);
  }

  // Simple function to perform a DELETE request
  delete(url, options) {
    return this.performRequest(url, 'DELETE', options);
  }

  // Simple function to perform a GET request
  request(url) {
    return this.performRequest(url, 'GET');
  }

  /**
   * performRequest performs an HTTP request with authorization headers.
   *
   * @param {string} url - The URL to which the request is sent.
   * @param {string} method - The HTTP method to use for the request (e.g., GET, POST).
   * @param {Object} [options] - Optional parameters for the request.
   * @return {Promise<Response>} - A promise that resolves with the HTTP response.
   */
  async performRequest(url, method, options = {}) {
    try {
      let isTrustedHost = this.isTrustedHost(url);
      let response = await this.fetchWithAuthentication(
        url,
        method,
        options,
        isTrustedHost,
      );
      return this.handleResponse(response, isTrustedHost);
    } catch (error) {
      this.logging.pushError(error, 'performRequest');
      throw error;
    }
  }

  async fetchWithAuthentication(
    url,
    method,
    options = {},
    isTrustedHost = this.isTrustedHost(url),
  ) {
    try {
      // block on token retrieval
      let tokenWrapper = await this.session.getToken();

      let headers = {
        ...this.buildAuthHeader(isTrustedHost, tokenWrapper),
        ...this.buildTenantHeader(url),
        ...options.headers,
      };

      const body =
        typeof options.data === 'string'
          ? options.data
          : JSON.stringify(options.data);

      return fetch(url, {
        method,
        headers,
        body: body || undefined,
      });
    } catch (error) {
      this.logging.pushError(error, 'fetchWithAuthentication', {
        requestUri: url,
      });

      // Rethrow for callers to handle (or not lol)
      throw error;
    }
  }

  // isTrustedHost returns whether url's host is configured to receive our
  // Authorization header, and so is trusted to prove token validity via a
  // successful response.
  isTrustedHost(url) {
    return this.trustedHosts.includes(new URL(url).hostname);
  }

  // buildAuthHeader returns the Authorization header for trusted hosts.
  buildAuthHeader(isTrustedHost, tokenWrapper) {
    if (!isTrustedHost) {
      return {};
    }

    if (isEmpty(tokenWrapper?.token)) {
      throw new Error(`no token, refusing to fetch without token`);
    }

    return { Authorization: `Bearer ${tokenWrapper.token}` };
  }

  // buildTenantHeader sets the x-tenant-id header to the current user's active school.
  buildTenantHeader(url) {
    if (!this.useActiveSchoolID) {
      return {};
    }

    const tid = this.get('session.activeSchool');
    if (!isEmpty(tid)) {
      return { 'X-Tenant-Id': tid };
    }

    this.logging.pushLog(
      'no activeSchool, request being sent without tid',
      'fetchWithAuthentication',
      { level: LogLevel.WARN, requestUri: url },
    );
    throw new Error(`no activeSchool, refusing to fetch without tid`);
  }

  handleResponse(response, isTrustedHost) {
    if (!response.ok) {
      // An unauthorized response from a trusted host means our access token
      // was rejected. That can be transient (clock skew after wake, key
      // rotation, backend blip) or terminal (session revoked server-side),
      // so rather than logging out (which kills the IdP SSO session and
      // forces a full credential login), re-authenticate via the authorize
      // endpoint: a still-valid SSO session silently re-issues tokens; a
      // revoked one lands on the login page. A counter, cleared only by a
      // successful API response, catches an API that persistently rejects
      // fresh tokens and falls back to logout. A 401 from an untrusted host
      // says nothing about our tokens (no Authorization header was sent) and
      // must not purge the session.
      if (response.status === 401) {
        if (isTrustedHost && !this.session.reauthInProgress) {
          const maxReauthAttempts = 3;
          const underLimit = this.session.consumeGuardAttempt(
            'session:fetch401Count',
            maxReauthAttempts,
          );
          if (underLimit) {
            this.logging.pushError(
              'unauthorized detected, re-authenticating',
              'handleResponse',
            );
            this.session.reauthenticate('fetch401');
          } else {
            this.logging.pushError(
              `unauthorized persisted across ${maxReauthAttempts} re-auth attempts, logging out`,
              'handleResponse',
            );
            this.session.logout('fetch');
          }
        }
        throw new Error('Unauthorized');
      }

      let requestError;
      if (response.status === 400) {
        requestError = 'Bad Request';
      } else if (response.status === 403) {
        requestError = 'Forbidden';
      } else if (response.status === 404) {
        requestError = 'Not Found';
      } else if (response.status === 500) {
        requestError = 'Server Error';
      } else {
        requestError = `HTTP error! status: ${response.status}`;
      }

      let error = new Error(requestError);
      // Expose the HTTP status so callers can branch on it robustly rather
      // than string-matching the message (e.g. 409 conflict handling).
      error.status = response.status;
      throw error;
    }
    // A successful response from a trusted (authenticated) host proves our
    // tokens work: reset the 401 re-auth loop guard, along with the
    // reauthenticate() loop guard it may have driven, so a recovered
    // fetch401 cycle doesn't leave reauthCount primed to trip
    // logout('reauthLoop') on some unrelated future reauth. A response from
    // an untrusted host says nothing about our tokens (no Authorization
    // header was even sent) and must not mask a trusted endpoint that is
    // persistently 401ing.
    if (isTrustedHost) {
      sessionStorage.removeItem('session:fetch401Count');
      sessionStorage.removeItem('session:reauthCount');
    }

    // No content in response (204)
    if (response.status === 204) {
      return null;
    }

    return response.json();
  }
}
